/**
 * ItemRevisionsTimelineDialog - Linha do tempo das revisões de um item da vistoria.
 *
 * Exibe, em ordem decrescente, cada revisão (append-only) com autor, anexos,
 * localização e as decisões do revisor (aprovado/reprovado com motivo).
 * Leitura via RPC `get_checklist_answer_revisions` (tenant + permissão validados no banco).
 *
 * @version 1.0.0
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial.
 */
import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Check, History, Loader2, MapPin, X, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ImageLightbox } from '@/components/tenant/chat/ImageLightbox';
import { cn } from '@/lib/utils';
import { signedInspectionUrl, useItemRevisions, type ItemRevision, type ItemReviewEvent } from '@/hooks/useVehicleInspections';

const fmt = (d: string) => format(new Date(d), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });

/** Entrada unificada da linha do tempo (revisão enviada ou decisão do revisor). */
type Entry = { kind: 'revision'; at: string; rev: ItemRevision } | { kind: 'review'; at: string; ev: ItemReviewEvent };

/** Miniatura de anexo com URL assinada do bucket privado. */
function Thumb({ path, mime, name, onOpen }: { path: string; mime: string | null; name: string; onOpen: (u: string) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const isImg = (mime ?? '').startsWith('image/');
  useEffect(() => { let a = true; signedInspectionUrl(path).then((u) => a && setUrl(u)); return () => { a = false; }; }, [path]);
  return (
    <button type="button" disabled={!url} title={name}
      onClick={() => url && (isImg ? onOpen(url) : window.open(url, '_blank', 'noopener'))}
      className="h-16 w-16 rounded-md border bg-muted overflow-hidden flex items-center justify-center">
      {isImg && url ? <img src={url} alt={name} className="h-full w-full object-cover" /> : <FileText className="h-5 w-5 text-muted-foreground" />}
    </button>
  );
}

export function ItemRevisionsTimelineDialog({ answerId, itemName, onOpenChange }: {
  answerId: string | null; itemName: string; onOpenChange: (o: boolean) => void;
}) {
  const { data, isLoading } = useItemRevisions(answerId);
  const [lightbox, setLightbox] = useState<string | null>(null);

  // Mescla revisões e decisões em ordem cronológica decrescente
  const entries = useMemo<Entry[]>(() => {
    if (!data) return [];
    return [
      ...data.revisions.map((rev) => ({ kind: 'revision' as const, at: rev.created_at, rev })),
      ...data.reviews.map((ev) => ({ kind: 'review' as const, at: ev.created_at, ev })),
    ].sort((a, b) => b.at.localeCompare(a.at));
  }, [data]);

  return (
    <Dialog open={!!answerId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85dvh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 py-3 border-b bg-muted/40 text-left">
          <DialogTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4 text-primary" />Histórico de revisões</DialogTitle>
          <p className="text-xs text-muted-foreground">{itemName}</p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma revisão registrada.</p>
          ) : (
            <ol className="relative ml-2 border-l space-y-4">
              {entries.map((e, i) => e.kind === 'revision' ? (
                <li key={`r-${e.rev.id}`} className="ml-4">
                  <span className={cn('absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-background', e.rev.is_current ? 'bg-primary' : 'bg-muted-foreground/40')} />
                  <div className={cn('rounded-lg border p-3 space-y-2', e.rev.is_current && 'border-primary/40 bg-primary/5')}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">Revisão {e.rev.revision_number}</span>
                      {e.rev.is_current && <Badge variant="secondary" className="text-[10px]">Vigente</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">{fmt(e.rev.created_at)} · {e.rev.author_name ?? 'Sistema'}</p>
                    {e.rev.value !== null && e.rev.value !== '' && e.rev.value !== undefined && (
                      <p className="text-xs">Valor: <span className="font-medium">{Array.isArray(e.rev.value) ? e.rev.value.join(', ') : String(e.rev.value)}</span></p>
                    )}
                    {e.rev.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {e.rev.attachments.map((a) => <Thumb key={a.id} path={a.storage_path} mime={a.mime_type} name={a.file_name} onOpen={setLightbox} />)}
                      </div>
                    )}
                    {e.rev.geo_lat != null && e.rev.geo_lng != null && (
                      <a className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline" target="_blank" rel="noopener noreferrer"
                        href={`https://www.google.com/maps/search/?api=1&query=${e.rev.geo_lat},${e.rev.geo_lng}`}>
                        <MapPin className="h-3 w-3" />Ver localização
                      </a>
                    )}
                  </div>
                </li>
              ) : (
                <li key={`e-${i}`} className="ml-4">
                  <span className={cn('absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-background', e.ev.event_type === 'item_approved' ? 'bg-success' : 'bg-destructive')} />
                  <p className={cn('text-sm font-medium flex items-center gap-1', e.ev.event_type === 'item_approved' ? 'text-success' : 'text-destructive')}>
                    {e.ev.event_type === 'item_approved' ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                    {e.ev.event_type === 'item_approved' ? 'Aprovado' : 'Reprovado'}
                  </p>
                  <p className="text-xs text-muted-foreground">{fmt(e.ev.created_at)} · {e.ev.actor_name ?? 'Sistema'}</p>
                  {e.ev.reason && <p className="text-xs mt-0.5">Motivo: {e.ev.reason}</p>}
                </li>
              ))}
            </ol>
          )}
        </div>
        {lightbox && <ImageLightbox open onClose={() => setLightbox(null)} imageUrl={lightbox} caption={itemName} />}
      </DialogContent>
    </Dialog>
  );
}
